import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import ServiceGrid from "@/components/ServiceGrid";
import Faq from "@/components/Faq";
import { MaskLines, Reveal, SectionHead } from "@/components/Reveal";
import { CITIES, getCity, cityImage, CITY_IMAGES, PHONE, PHONE_HREF, EMAIL_HREF } from "@/data";
import NotFound from "./NotFound";

const CityPage = () => {
  const { slug } = useParams();
  const city = getCity(slug);
  if (!city) return <NotFound />;
  const idx = CITIES.indexOf(city);
  const nearby = city.nearby.map(getCity).filter(Boolean);

  return (
    <div data-testid={`city-page-${city.slug}`}>
      <Seo
        title={`Security Guards ${city.name} | SIA Security Services ${city.name} | Xtreme Protection`}
        description={`SIA-licensed security guards in ${city.name}, ${city.county}. Manned guarding, mobile patrols, CCTV monitoring, door supervisors & event security across ${city.name}. 24/7 — call +44 2046 402840.`}
        path={`/locations/${city.slug}`}
        altCy={`/cy/lleoliadau/${city.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Security Guard Services",
          provider: { "@type": "LocalBusiness", name: "Xtreme Protection — Wales Security Services", telephone: "+44 2046 402840", email: "admin@xtremeprotection.uk" },
          areaServed: { "@type": "City", name: `${city.name}, ${city.county}, Wales` },
        }}
      />

      <section className="xp-noise relative overflow-hidden border-b border-[#1C2027] pb-16 pt-32">
        <div className="absolute inset-0">
          <img src={CITY_IMAGES[city.slug]?.url || cityImage(idx)} alt={CITY_IMAGES[city.slug]?.alt || `${city.name}, ${city.county} — protected by Xtreme Protection security services`} className="h-full w-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0C10] via-[#0A0C10]/80 to-[#0A0C10]/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-[#F5A800]" />
            Security Operations // {city.county} // {city.region}
          </p>
          <MaskLines
            className="font-condensed mt-6 text-5xl font-bold uppercase leading-[0.9] tracking-tight text-white sm:text-6xl lg:text-7xl"
            lines={["Security Guard Services", `In ${city.name}`]}
          />
          <Reveal delay={0.35}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#C8CCD2] sm:text-lg">{city.tag}. SIA-licensed officers, verified patrols and 24/7 command support across {city.name} and {city.county}.</p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={PHONE_HREF} data-testid="city-call-btn" className="group flex items-center gap-2 bg-[#F5A800] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-white">
                Call {PHONE}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link to="/services" data-testid="city-services-btn" className="flex items-center gap-2 border border-white/25 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-[#F5A800] hover:text-[#F5A800]">
                All 17 Services
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <div>
            <SectionHead index="01" eyebrow={`${city.name} Security`} title={`Security guards and services in ${city.name}`} testid="city-intro-heading" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-[#9BA1AB]">{city.intro}</p>
            </Reveal>
            <h3 className="font-condensed mt-10 text-2xl font-bold uppercase tracking-tight text-white">The {city.name} risk landscape</h3>
            <Reveal delay={0.1}>
              <p className="mt-4 text-base leading-relaxed text-[#9BA1AB]">{city.risk}</p>
            </Reveal>
            <h3 className="font-condensed mt-10 text-2xl font-bold uppercase tracking-tight text-white">Deploying across {city.county}</h3>
            <Reveal delay={0.1}>
              <p className="mt-4 text-base leading-relaxed text-[#9BA1AB]">{city.deploy}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="border border-[#1C2027] bg-[#10131A] p-6">
              <h3 className="font-condensed text-xl font-bold uppercase tracking-wide text-white">Areas covered in {city.name}</h3>
              <ul className="mt-4 space-y-2.5" data-testid="city-areas-list">
                {city.areas.map((a) => (
                  <li key={a} className="flex items-center gap-2.5 text-sm text-[#9BA1AB]">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#F5A800]" />
                    {a}
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-t border-[#1C2027] pt-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">Rapid deployment across {city.county}</p>
                <p className="mt-2 text-sm text-[#9BA1AB]">Urgent guard deployment typically within 24 hours anywhere in {city.name} and the surrounding area.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead index="02" eyebrow="Full Arsenal" title={`Every security service, available in ${city.name}`} testid="city-services-heading" />
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#9BA1AB]">
            All 17 Xtreme Protection services deploy in {city.name} — click any service for full detail on standards, delivery and Wales-wide coverage.
          </p>
          <div className="mt-10">
            <ServiceGrid />
          </div>
        </div>
      </section>

      {nearby.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <SectionHead index="03" eyebrow="Nearby Coverage" title={`Security services near ${city.name}`} testid="city-nearby-heading" />
          <div className="mt-8 grid gap-px border border-[#1C2027] bg-[#1C2027] sm:grid-cols-2 lg:grid-cols-4">
            {nearby.map((n, i) => (
              <Reveal key={n.slug} delay={i * 0.07} y={14} className="h-full">
                <Link to={`/locations/${n.slug}`} data-testid={`nearby-city-${n.slug}`} className="group flex h-full items-center justify-between bg-[#10131A] p-5 transition-colors duration-300 hover:bg-[#161A22]">
                  <div>
                    <p className="font-condensed text-lg font-bold uppercase tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">Security {n.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">{n.county}</p>
                  </div>
                  <MapPin className="h-4 w-4 text-[#F5A800]/60 transition-transform duration-300 group-hover:scale-125" />
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <SectionHead index="04" eyebrow="Local Briefing" title={`${city.name} security questions, answered`} testid="city-faq-heading" />
          <div className="mt-10">
            <Faq items={city.faqs} idPrefix="city-faq" />
          </div>
        </div>
      </section>

      <section className="bg-[#F5A800] py-12" data-testid="city-cta-band">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:flex-row lg:items-center">
          <h2 className="font-condensed text-3xl font-bold uppercase leading-none text-[#0A0C10] sm:text-4xl">Book your free {city.name} risk assessment</h2>
          <div className="flex flex-wrap gap-3">
            <motion.a whileTap={{ scale: 0.97 }} href={PHONE_HREF} data-testid="city-cta-call" className="bg-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#F5A800] transition-colors duration-300 hover:bg-[#1A1E26]">
              Call {PHONE}
            </motion.a>
            <a href={EMAIL_HREF} data-testid="city-cta-email" className="border-2 border-[#0A0C10] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-[#0A0C10] hover:text-[#F5A800]">
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
